import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { token } from "../App";
import getHost, { link } from "./host-store";

const AuthContext = React.createContext({
    setRerender: () => {},
    rerender: false,
    adsNumber: 0,
    isUserId: 0,
    userData: {},
    setRefreshData: () => {},
    setLocation: () => {},
    location: {},
    onDeleteProfile: () => {},
});

export const AuthContextProvide = ({ children }) => {
    const [isUserId, setUserId] = useState(0);
    const [userData, setUserData] = useState({});
    const [adsNumber, setAdsNumber] = useState(0);
    const [notificationsCount, setNotificationsCount] = useState(0);
    const [requestsCount, setRequestsCount] = useState(0);
    const [responseCount, setResponseCount] = useState(0);
    const [rerender, setRerender] = useState(false);
    const [location, setLocation] = useState(null);
    const [refreshData, setRefreshData] = useState(false);
    const [currentPage, setCurrentPage] = useState("signIn");
    const [categories, setCategories] = useState([]);

    const seekType = useSelector((state) => state.adsSearchLogic.statusAds);
    const locationRouter = useLocation();

    const defaultMoscow = {
        country: "Россия",
        country_id: 1,
        region: "Москва",
        region_id: 5,
        city: "Москва",
        city_id: 436,
        alias: "moskva",
    };

    useEffect(() => {
        const aliasFromUrl =
            locationRouter.pathname.split("/").filter(Boolean)[0] || null;
        const localData = JSON.parse(localStorage.getItem("location"));

        async function resolveLocation() {
            try {
                // 1) если есть alias в URL
                if (aliasFromUrl) {
                    const res = await fetch(
                        `${link}/city/by-alias?alias=${aliasFromUrl}&expand=region,region.country`,
                        { headers: { Accept: "application/json", Authorization: token } }
                    ).then((r) => r.json());

                    if (res.success) {
                        const data = {
                            country: res.data.region.country.name,
                            country_id: res.data.region.country.id,
                            region: res.data.region.name,
                            region_id: res.data.region.id,
                            city: res.data.name,
                            city_id: res.data.id,
                            alias: res.data.alias,
                        };
                        localStorage.setItem("location", JSON.stringify(data));
                        setLocation(data);
                        return;
                    }
                }

                if (localData) {
                    setLocation(localData);
                    return;
                }

                const geoRes = await fetch(
                    getHost({
                        controller: "city",
                        action: "geo",
                        expand: "region, region.country",
                    }),
                    { headers: { Accept: "application/json", Authorization: token } }
                ).then((r) => r.json());

                if (geoRes.success) {
                    const data = {
                        country: geoRes.data.region.country.name,
                        country_id: geoRes.data.region.country.id,
                        region: geoRes.data.region.name,
                        region_id: geoRes.data.region.id,
                        city: geoRes.data.name,
                        city_id: geoRes.data.id,
                        alias: geoRes.data.alias,
                    };
                    localStorage.setItem("location", JSON.stringify(data));
                    setLocation(data);
                    return;
                }

                // 4) fallback Москва
                localStorage.setItem("location", JSON.stringify(defaultMoscow));
                setLocation(defaultMoscow);
            } catch (err) {
                console.log(err);
                localStorage.setItem("location", JSON.stringify(defaultMoscow));
                setLocation(defaultMoscow);
            }
        }

        resolveLocation();
    }, [locationRouter.pathname]);

    useEffect(() => {
        if (!location) return;
        fetch(
            getHost({
                controller: "user",
                action: "identity",
                expand: "account, wallet, organization",
            }),
            {
                headers: {
                    Accept: "application/json",
                    Authorization: token,
                },
            }
        )
            .then((res) => res.json())
            .then((result) => {
                if (result.success) {
                    setUserData(result.data);
                    setUserId(result.data.id);
                }
            })
            .catch((e) => console.log(e));
    }, [refreshData, location]);

    useEffect(() => {
        if (!location || !userData?.account?.role) return;

        const fetchCounts = async () => {
            try {
                const [notifRes, responseRes, requestRes, adsRes] = await Promise.all([
                    fetch(`${link}/notification/amount-new?user_id=${isUserId}`, {
                        headers: { Accept: "application/json", Authorization: token },
                    }).then((r) => r.json()),

                    fetch(
                        `${link}/order/amount-confirmed-by-user?seek=${seekType}&filter[user_id]=${isUserId}`,
                        { headers: { Accept: "application/json", Authorization: token } }
                    ).then((r) => r.json()),

                    fetch(
                        `${link}/order/amount-by-user?seek=${seekType}&filter=${isUserId}`,
                        { headers: { Accept: "application/json", Authorization: token } }
                    ).then((r) => r.json()),

                    fetch(
                        `${link}/category/account-list?seek=${seekType}&city_id=${location.city_id}`,
                        { headers: { Accept: "application/json", Authorization: token } }
                    ).then((r) => r.json()),
                ]);

                if (notifRes.success) setNotificationsCount(notifRes.data.amountNew);
                if (responseRes.success) setResponseCount(responseRes.data);
                if (requestRes.success) setRequestsCount(requestRes.data);
                if (adsRes.success) {
                    const adsNumber = adsRes.data.reduce(
                        (a, b) => a + Number(b.objectsCountByType),
                        0
                    );
                    setAdsNumber(adsNumber);
                }
            } catch (err) {
                console.log(err);
            }
        };

        fetchCounts();
    }, [isUserId, rerender, userData?.account?.role, seekType, location]);

    useEffect(() => {
        if (!location) return;
        fetch(`${link}/category?pagination=0&sort=title&city_id=${location.city_id}`, {
            headers: { Accept: "application/json", Authorization: token },
        })
            .then((res) => res.json())
            .then((result) => {
                if (result.success) setCategories(result.data);
            })
            .catch((e) => console.log(e));
    }, [location]);

    const onDeleteProfile = () => {
        fetch(getHost({ controller: "user", action: userData.id }), {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: token,
            },
        })
            .then((res) => res.json())
            .then((result) => {
                if (result.success) {
                    localStorage.removeItem("token");
                    window.location.replace("/");
                }
            })
            .catch((e) => console.log(e));
    };

    if (!location) {
        return null;
    }

    return (
        <AuthContext.Provider
            value={{
                isUserId,
                userData,
                adsNumber,
                setRerender,
                rerender,
                notificationsCount,
                setNotificationsCount,
                requestsCount,
                responseCount,
                setRefreshData,
                location,
                setLocation,
                onDeleteProfile,
                currentPage,
                setCurrentPage,
                categories,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;